import { isEmpty } from 'lodash-es';
import has from '@dojo/framework/core/has';
import { createICacheMiddleware } from '@dojo/framework/core/middleware/icache';
import { create, destroy, w } from '@dojo/framework/core/vdom';
import { LandingPage } from '@holmescorp/landing-page';
import { lccstate, locationChangeProcess, recommended, userstate, whichActiveViewProcess } from '@holmescorp/lcc-state';
import { subscribe } from '@holmescorp/pub-sub';
import { AssessView } from './assess-view';
import { AssessmentChooser } from './assessment-chooser';
import { CarouselView } from './carousel-view';
import { ExploreView } from './explore-view';
import { MicroView } from './micro-view';
import { PracticeView } from './practice-view';
import { WhereYouLeftOff } from './where-you-left-off';
const icache = createICacheMiddleware();
const factory = create({ icache, recommended, lccstate, userstate, destroy });
export const ActiveView = factory(({ middleware: { icache, recommended, lccstate, userstate, destroy } }) => {
    const useAssessView = !has('no-explore-assess');
    const useStudyView = !has('no-explore-study');
    const usePassView = !has('no-explore-pass');
    const useLandingView = has('use-landing-page');
    const useCarouselView = has('use-carousel-view');
    const useMicroView = has('use-micro-view');
    const useWYLO = has('use-wylo');
    const firstViewtype = useAssessView ? 'assess' : useStudyView ? 'study' : 'practice';
    const { get: uget, path: upath } = userstate;
    const trackingwylo = uget(upath('toolstate', 'trackingwylo'));
    let alwaysResume = null;
    let alwaysResumePromise = null;
    const waitForAlwaysResume = async () => {
        if (alwaysResume) {
            return;
        }
        if (!alwaysResumePromise) {
            alwaysResumePromise = new Promise((resolve) => {
                const checkInterval = setInterval(() => {
                    if (alwaysResume) {
                        clearInterval(checkInterval);
                        resolve();
                    }
                }, 100);
            });
        }
        return alwaysResumePromise;
    };
    const wyloWidget = w(WhereYouLeftOff, {
        onAlwaysResume: (resumeFunction) => {
            alwaysResume = resumeFunction;
        }
    });
    const { complete: landingCompleted = false } = uget(upath('toolstate', 'landing'));
    const useLanding = useLandingView && !landingCompleted;
    const selectView = (viewtype) => {
        let view;
        lccstate.run(whichActiveViewProcess)({ whichActive: viewtype });
        switch (viewtype) {
            case 'assess':
                if (useAssessView) {
                    userstate.run(locationChangeProcess)({ location: '/explore-assess' });
                    view = has('use-assessment-chooser')
                        ? w(AssessmentChooser, { switchView, setTitle, xqa: 'assessment-chooser' })
                        : w(AssessView, { switchView });
                }
                else {
                    return selectView(firstViewtype);
                }
                break;
            case 'practice':
                if (usePassView) {
                    userstate.run(locationChangeProcess)({ location: '/explore-practice' });
                    view = w(PracticeView, { switchView });
                }
                else {
                    return selectView(firstViewtype);
                }
                break;
            case 'study':
            default:
                if (useStudyView) {
                    userstate.run(locationChangeProcess)({ location: '/explore' });
                    view = useMicroView
                        ? w(MicroView, { switchView })
                        : useCarouselView
                            ? w(CarouselView, { switchView })
                            : w(ExploreView, { switchView });
                }
                else {
                    return selectView(firstViewtype);
                }
                break;
        }
        return view;
    };
    const switchView = (viewtype) => {
        icache.set('view', selectView(viewtype));
    };
    const setTitle = (title) => {
        if (title) {
            document.title = title;
        }
    };
    const view = icache.getOrSet('view', () => {
        const h = subscribe('active-view', (topic, viewtype) => {
            switchView(viewtype);
        });
        destroy(() => {
            h.destroy();
        });
        const { get, path } = lccstate;
        const whichView = get(path('whichActiveView'), true);
        lccstate.run(whichActiveViewProcess)({ whichActive: null });
        if (useWYLO) {
            if (!isEmpty(trackingwylo.tracking)) {
                if (trackingwylo.always_resume) {
                    (async () => {
                        await waitForAlwaysResume();
                        if (alwaysResume) {
                            return alwaysResume();
                        }
                        else {
                            console.warn('External play function is still loading.');
                        }
                    })();
                }
                return wyloWidget;
            }
        }
        if (useLanding) {
            userstate.run(locationChangeProcess)({ location: '/' });
            return w(LandingPage, {});
        }
        else if (whichView) {
            return selectView(whichView);
        }
        else if ((recommended.allcomplete() && usePassView) || (usePassView && !useStudyView)) {
            userstate.run(locationChangeProcess)({ location: '/explore-practice' });
            return w(PracticeView, { switchView });
        }
        else if ((recommended.assessmentsComplete() && useStudyView) || (useStudyView && !useAssessView)) {
            userstate.run(locationChangeProcess)({ location: '/explore' });
            return useMicroView
                ? w(MicroView, { switchView })
                : useCarouselView
                    ? w(CarouselView, { switchView })
                    : w(ExploreView, { switchView });
        }
        else if (useAssessView) {
            userstate.run(locationChangeProcess)({ location: '/explore-assess' });
            return has('use-assessment-chooser')
                ? w(AssessmentChooser, { switchView, setTitle, xqa: 'assessment-chooser' })
                : w(AssessView, { switchView });
        }
    });
    return view;
});
